// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import { AuthFacade } from '@amwager/authentication/data-access';
import {
  AccountHistoryRow,
  AccountHistoryService,
  ACCOUNT_HISTORY_COMING_SOON_MSG,
  ProfileService
} from '@amwager/account/data-access';
import {
  MatNotificationService,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';

// Local file
import { StatementsComponent } from '../history/statements/statements.component';
import { BetSummaryComponent } from '../history/bet-summary/bet-summary.component';
import { OverviewComponent } from '../history/overview/overview.component';

/**
 * Account history rows (Statements, Bet Summary, Overview) inside the
 * profile panel. Ported from the Ionic account-history modal; row state
 * and descriptors live in AccountHistoryService.
 */
@Component({
  selector: 'feature-mat-account-history',
  templateUrl: './account-history.component.html',
  styleUrls: ['./account-history.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatExpansionModule,
    StatementsComponent,
    BetSummaryComponent,
    OverviewComponent,
    TranslatePipe
  ]
})
export class AccountHistoryComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  readonly rows = this.accountHistoryService.rows;

  activeRow: AccountHistoryRow = null;

  constructor(
    private readonly accountHistoryService: AccountHistoryService,
    private readonly profileService: ProfileService,
    private readonly authFacade: AuthFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.accountHistoryService.activeRow$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((row) => {
        this.activeRow = row;
        this.cdRef.markForCheck();
      });

    this.authFacade.isLogoutSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((isLogout?: boolean) => {
        if (!isLogout) return;
        this.accountHistoryService.closeAllRows();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Handler for expansion-panel (opened)/(closed) events
   * @param {Exclude<AccountHistoryRow, null>} rowId Row identifier
   * @param {boolean} expanded True if the panel was just opened
   * @returns {void}
   */
  onRowToggle(rowId: Exclude<AccountHistoryRow, null>, expanded: boolean): void {
    if (expanded) {
      this.accountHistoryService.toggleRow(rowId);
    } else if (this.activeRow === rowId) {
      this.accountHistoryService.closeAllRows();
    }
  }

  /**
   * Non-expandable row click — shows a "coming soon" alert
   * @returns {void}
   */
  openComingSoon(): void {
    this.matNotificationService.showAlert(
      'Info',
      ACCOUNT_HISTORY_COMING_SOON_MSG,
      '',
      '',
      'Close'
    );
  }

  /**
   * Close Account History — collapse any open row and return the
   * profile panel to its default (Promotions) section.
   * @returns {void}
   */
  close(): void {
    this.accountHistoryService.closeAllRows();
    this.profileService.setActiveSection('default');
  }

  /**
   * trackBy helper for the row list
   * @param {number} _index Loop index
   * @param {{ id: string }} row Row descriptor
   * @returns {string} Stable identity key
   */
  trackByRowId(_index: number, row: { id: string }): string {
    return row.id;
  }

  /**
   * True when this row should be hidden (another row is expanded)
   * @param {string} rowId Row identifier
   * @returns {boolean} True when the row should be hidden
   */
  isRowHidden(rowId: string): boolean {
    return this.activeRow !== null && this.activeRow !== rowId;
  }
}
